import { LoadingButton } from '@mui/lab';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  InputAdornment,
  TextField,
} from '@mui/material';
import { useFormik } from 'formik';
import { treasuryWithdrawFormFields as F } from '../../../../forms/fields/formFields/treasuryWithdraw';
import { treasuryWithdrawSchema } from '../../../../forms/validationSchema/formSchema/treasuryWithdraw';
import { CreateCompanyWithdrawRequestRequest } from '../../../../types/b2tRatibiPlus';

type TreasuryWithdrawDialogProps = {
  open: boolean;
  isSaving: boolean;
  onClose: () => void;
  onSave: (payload: CreateCompanyWithdrawRequestRequest) => void;
};

const TEXT_FIELDS: { field: F; label: string }[] = [
  { field: F.beneficiaryName, label: 'Beneficiary Name' },
  { field: F.beneficiaryIban, label: 'IBAN' },
  { field: F.beneficiarySwiftCode, label: 'SWIFT Code' },
];

/** The form, mounted per open — so every open starts empty and validated. */
const TreasuryWithdrawForm = ({ isSaving, onClose, onSave }: Omit<TreasuryWithdrawDialogProps, 'open'>) => {
  const { handleSubmit, values, handleChange, handleBlur, touched, errors, isValid } = useFormik({
    initialValues: {
      [F.amount]: '',
      [F.beneficiaryName]: '',
      [F.beneficiaryIban]: '',
      [F.beneficiarySwiftCode]: '',
      [F.reason]: '',
    },
    validationSchema: treasuryWithdrawSchema,
    validateOnMount: true,
    onSubmit: (formValues) =>
      onSave({
        amount: Number(formValues[F.amount]),
        reason: formValues[F.reason].trim(),
        beneficiaryName: formValues[F.beneficiaryName].trim(),
        beneficiaryIban: formValues[F.beneficiaryIban].trim(),
        beneficiarySwiftCode: formValues[F.beneficiarySwiftCode].trim(),
      }),
  });

  const fieldError = (field: F) => (touched[field] && errors[field]) || undefined;

  return (
    <form onSubmit={handleSubmit}>
      <DialogTitle>New Withdraw Request</DialogTitle>
      <DialogContent>
        <Grid container spacing={2} sx={{ pt: 1 }}>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              required
              size="small"
              id={F.amount}
              name={F.amount}
              label="Amount"
              type="number"
              value={values[F.amount]}
              onChange={handleChange}
              onBlur={handleBlur}
              error={Boolean(fieldError(F.amount))}
              helperText={fieldError(F.amount)}
              InputProps={{ startAdornment: <InputAdornment position="start">SAR</InputAdornment> }}
            />
          </Grid>
          {TEXT_FIELDS.map(({ field, label }) => (
            <Grid item xs={12} md={6} key={field}>
              <TextField
                fullWidth
                required
                size="small"
                id={field}
                name={field}
                label={label}
                value={values[field]}
                onChange={handleChange}
                onBlur={handleBlur}
                error={Boolean(fieldError(field))}
                helperText={fieldError(field)}
                inputProps={{ maxLength: 255 }}
              />
            </Grid>
          ))}
          <Grid item xs={12}>
            <TextField
              fullWidth
              required
              multiline
              minRows={2}
              size="small"
              id={F.reason}
              name={F.reason}
              label="Reason"
              value={values[F.reason]}
              onChange={handleChange}
              onBlur={handleBlur}
              error={Boolean(fieldError(F.reason))}
              helperText={fieldError(F.reason)}
              inputProps={{ maxLength: 255 }}
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <LoadingButton type="submit" variant="contained" loading={isSaving} disabled={!isValid}>
          Create Request
        </LoadingButton>
      </DialogActions>
    </form>
  );
};

/** A new treasury withdraw request — the amount is held on the company treasury bank until approved or rejected. */
export const TreasuryWithdrawDialog = ({ open, ...props }: TreasuryWithdrawDialogProps) => (
  <Dialog open={open} onClose={props.onClose} maxWidth="sm" fullWidth>
    {open && <TreasuryWithdrawForm {...props} />}
  </Dialog>
);
